/**
 * Trace detail page — header card + status banner + react-flow diagram +
 * per-step drawer.
 *
 * Spec: dev/projects/sl2-sstv2-live-trace.md §4.2
 *
 * Polls the trace endpoint every 10 s while the trace is still in-progress
 * (so the live diagram updates without manual refresh).
 */
import { ArrowBack, ExpandMore, OpenInNew } from "@mui/icons-material"
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Link,
  Stack,
  Typography,
} from "@mui/material"
import { FC, useCallback, useMemo, useState } from "react"
import { JsonView } from "react-json-view-lite"
import { Link as RouterLink, useNavigate, useParams } from "react-router-dom"

import "react-json-view-lite/dist/index.css"

import SaLayout from "../../components/SaLayout"
import { buildCloudWatchInsightsUrl, buildXrayTraceUrl } from "./cloudwatchUrl"
import { fmtBytes, fmtDuration } from "./formatters"
import { Sstv2RawEventPanel } from "./Sstv2RawEventPanel"
import { Sstv2TraceHeaderCard } from "./Sstv2TraceHeaderCard"
import { useGetTraceByIdQuery } from "./sstv2TraceApi"
import Sstv2TraceStepDrawer from "./Sstv2TraceStepDrawer"
import Sstv2TraceTimeline from "./Sstv2TraceTimeline"
import { getTraceStatusStyle } from "./traceColors"
import type { TraceItem, TraceStepItem } from "./types"

const POLL_INTERVAL_MS = 10_000

const StatusBanner: FC<{ trace: TraceItem }> = ({ trace }) => {
  const style = getTraceStatusStyle(trace.status)
  let detail = ""
  if (trace.status === "succeeded") {
    detail = trace.durationMs != null ? `in ${fmtDuration(trace.durationMs)}` : ""
  } else if (trace.status === "failed") {
    detail = `at step ${trace.stepCount}`
  } else if (trace.status === "in-progress") {
    detail = `${trace.stepCount} step${trace.stepCount === 1 ? "" : "s"} so far`
  }
  return (
    <Box
      sx={{
        bgcolor: style.background,
        color: style.color,
        px: 2,
        py: 1.25,
        borderRadius: 1,
        mb: 2,
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        ...(style.pulse
          ? {
              animation: "sstv2TraceBannerPulse 1.5s ease-in-out infinite",
              "@keyframes sstv2TraceBannerPulse": {
                "0%, 100%": { opacity: 1 },
                "50%": { opacity: 0.65 },
              },
            }
          : {}),
      }}
    >
      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
        {style.label}
      </Typography>
      {detail && <Typography variant="body2">{detail}</Typography>}
      {trace.errorCount > 0 && (
        <Chip
          size="small"
          label={`${trace.errorCount} error${trace.errorCount === 1 ? "" : "s"}`}
          color="error"
          sx={{ ml: "auto" }}
        />
      )}
    </Box>
  )
}


/**
 * Trace-level error panel. Renders the header's `error` summary (mirrored from
 * the failing step) so the reader sees "what went wrong" without opening the
 * timeline. The HTTP fields (statusCode / responseBody / path) come from
 * partner errors like `Sstv2ApiError`. "View failing step" jumps straight to
 * the offending segment's drawer via `failedStepId`.
 */
const TraceErrorPanel: FC<{
  trace: TraceItem
  onViewFailingStep: (stepId: string) => void
}> = ({ trace, onViewFailingStep }) => {
  if (!trace.error) return null
  const { error, failedStepId } = trace
  return (
    <Box
      sx={{
        border: "1px solid",
        borderColor: "error.light",
        bgcolor: "rgba(211, 47, 47, 0.04)",
        borderRadius: 1,
        p: 2,
        mb: 2,
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="subtitle2" color="error" sx={{ fontWeight: 700 }}>
          {error.name}
        </Typography>
        {error.statusCode != null && (
          <Chip
            size="small"
            color={error.statusCode >= 500 ? "error" : "warning"}
            label={`HTTP ${error.statusCode}`}
          />
        )}
        <Box sx={{ flex: 1 }} />
        {failedStepId && (
          <Button
            size="small"
            variant="outlined"
            color="error"
            onClick={() => onViewFailingStep(failedStepId)}
          >
            View failing step
          </Button>
        )}
      </Stack>
      <Typography variant="body2" sx={{ mb: error.responseBody ? 1 : 0 }}>
        {error.message}
      </Typography>
      {error.path && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontFamily: "monospace", display: "block", mb: 1 }}
        >
          {error.path}
        </Typography>
      )}
      {error.responseBody && (
        <Box
          component="pre"
          sx={{
            bgcolor: "rgba(0, 0, 0, 0.04)",
            p: 1,
            borderRadius: 1,
            fontSize: 11,
            overflow: "auto",
            maxHeight: 200,
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            m: 0,
          }}
        >
          {error.responseBody}
        </Box>
      )}
    </Box>
  )
}

export const Sstv2TraceDetailPage: FC = () => {
  const { traceId } = useParams<{ traceId: string }>()
  const navigate = useNavigate()
  const [selectedStep, setSelectedStep] = useState<TraceStepItem | undefined>()
  const [drawerOpen, setDrawerOpen] = useState(false)

  // Poll while in-progress; otherwise no polling.
  const { data, isFetching, isError, error } = useGetTraceByIdQuery(
    { traceId: traceId ?? "" },
    {
      skip: !traceId,
      pollingInterval: POLL_INTERVAL_MS,
      // RTK lets us turn polling off conditionally via skipPollingIfUnfocused.
      // We keep polling on so the user sees fresh data even if backgrounded.
    },
  )

  const trace = data?.data?.trace
  const steps = useMemo(() => data?.data?.steps ?? [], [data])

  const handleStepClick = useCallback((step: TraceStepItem) => {
    setSelectedStep(step)
    setDrawerOpen(true)
  }, [])
  const handleDrawerClose = useCallback(() => setDrawerOpen(false), [])

  // Jump from the trace-level error panel to the failing step's drawer.
  // Prefer the last matching step by sequence (a stepId can recur across a
  // trace; the failure is the most recent occurrence).
  const handleViewFailingStep = useCallback(
    (stepId: string) => {
      const match = [...steps]
        .reverse()
        .find((s) => s.stepId === stepId && s.status === "failed")
        ?? [...steps].reverse().find((s) => s.stepId === stepId)
      if (match) {
        setSelectedStep(match)
        setDrawerOpen(true)
      }
    },
    [steps],
  )

  const cwUrl = traceId ? buildCloudWatchInsightsUrl(traceId) : ""
  const xrayUrl = trace?.xrayRootId ? buildXrayTraceUrl(trace.xrayRootId) : ""

  if (!traceId) {
    return (
      <SaLayout selectedMenuItemLabelKey="sstv2Api">
        <Box sx={{ p: 4 }}>
          <Typography>Missing trace id.</Typography>
        </Box>
      </SaLayout>
    )
  }

  return (
    <SaLayout selectedMenuItemLabelKey="sstv2Api">
      <Box sx={{ maxWidth: 1400, mx: "auto", px: { xs: 3, md: 5 }, py: { xs: 3, md: 4 } }}>
        {/* Top bar with back button */}
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
          <IconButton size="small" onClick={() => navigate("/sa/sstv2?tab=6")}>
            <ArrowBack fontSize="small" />
          </IconButton>
          <Typography variant="h5" sx={{ flex: 1, fontWeight: 700 }}>
            Trace
          </Typography>
          {isFetching && <CircularProgress size={18} />}
          {xrayUrl && (
            <Link
              href={xrayUrl}
              target="_blank"
              rel="noreferrer noopener"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
                fontSize: 13,
              }}
            >
              X-Ray
              <OpenInNew fontSize="inherit" />
            </Link>
          )}
          {cwUrl && (
            <Link
              href={cwUrl}
              target="_blank"
              rel="noreferrer noopener"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
                fontSize: 13,
              }}
            >
              CloudWatch
              <OpenInNew fontSize="inherit" />
            </Link>
          )}
        </Stack>

        {isError && (
          <Typography color="error" sx={{ mb: 2 }}>
            Error loading trace: {String(error)}
          </Typography>
        )}

        {!trace && !isFetching && !isError && (
          <Typography>Trace not found: {traceId}</Typography>
        )}

        {trace && (
          <>
            <StatusBanner trace={trace} />

            <TraceErrorPanel
              trace={trace}
              onViewFailingStep={handleViewFailingStep}
            />

            <Sstv2TraceHeaderCard
              trace={trace}
              xrayUrl={xrayUrl}
              rawPayloadSlot={
                trace.rawPayload != null ? (
                  <Accordion sx={{ mt: 2 }} disableGutters>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                      <Typography variant="body2">
                        Raw payload
                        {trace.rawPayloadTruncated && (
                          <Chip
                            size="small"
                            color="warning"
                            label={`truncated · original ${fmtBytes(
                              trace.rawPayloadOriginalSize,
                            )}`}
                            sx={{ ml: 1 }}
                          />
                        )}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      <Box sx={{ fontSize: 12 }}>
                        <JsonView data={trace.rawPayload as object} />
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                ) : null
              }
            />

            <Divider sx={{ mb: 2 }} />

            <Sstv2RawEventPanel trace={trace} />

            <Divider sx={{ mb: 2 }} />

            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Timeline ({steps.length} step{steps.length === 1 ? "" : "s"})
            </Typography>
            <Sstv2TraceTimeline
              steps={steps}
              onStepClick={handleStepClick}
              selectedStepSortKey={selectedStep?.sk}
            />
          </>
        )}

        <Sstv2TraceStepDrawer
          open={drawerOpen}
          step={selectedStep}
          traceId={traceId}
          onClose={handleDrawerClose}
        />
      </Box>
    </SaLayout>
  )
}

export default Sstv2TraceDetailPage
